/**
 * The clauses a broker can switch on for a leave and license draft. Each has a key, text in English,
 * Marathi and Hindi, and a default. The Marathi and Hindi text has not been read by a native speaker
 * or a lawyer yet (docs/BACKLOG.md). Nothing here is legal advice, and the draft says so.
 */
export const CLAUSE_KEYS = [
    'maintenance',
    'utilities',
    'subletting',
    'termination',
    'depositRefund',
    'useOfPremises',
    'inspection',
    'inventory',
    'policeVerification',
];
export const CLAUSES = {
    maintenance: {
        defaultOn: true,
        title: { en: 'Maintenance and repairs', mr: 'देखभाल आणि दुरुस्ती', hi: 'रखरखाव और मरम्मत' },
        text: {
            en: () => 'The Licensee will keep the premises clean and in good condition and will pay for small repairs caused by use. The Licensor will pay for structural repairs and for faults that are not caused by the Licensee.',
            mr: () => 'लायसन्सी जागा स्वच्छ आणि चांगल्या स्थितीत ठेवतील आणि वापरामुळे होणाऱ्या लहान दुरुस्तीचा खर्च करतील. इमारतीच्या रचनेची दुरुस्ती आणि लायसन्सीमुळे न झालेले बिघाड यांचा खर्च लायसन्सर करतील.',
            hi: () => 'लाइसेंसधारी परिसर को साफ़ और अच्छी हालत में रखेंगे और उपयोग से होने वाली छोटी मरम्मत का खर्च उठाएँगे। ढाँचे की मरम्मत और लाइसेंसधारी के कारण न हुई ख़राबियों का खर्च लाइसेंसदाता उठाएँगे।',
        },
    },
    utilities: {
        defaultOn: true,
        title: {
            en: 'Utilities and society charges',
            mr: 'वीज, पाणी आणि सोसायटी शुल्क',
            hi: 'बिजली, पानी और सोसायटी शुल्क',
        },
        text: {
            en: () => 'The Licensee will pay the electricity, water, gas and internet bills for the time they use the premises. The Licensor will pay the society maintenance charges and property tax, unless the parties write down something else here.',
            mr: () => 'लायसन्सी जागा वापरत असलेल्या काळातील वीज, पाणी, गॅस आणि इंटरनेट बिले भरतील. सोसायटी देखभाल शुल्क आणि मालमत्ता कर लायसन्सर भरतील, पक्षांनी येथे वेगळे लिहिले नसल्यास.',
            hi: () => 'लाइसेंसधारी जितने समय परिसर का उपयोग करेंगे, उतने समय के बिजली, पानी, गैस और इंटरनेट के बिल भरेंगे। सोसायटी रखरखाव शुल्क और संपत्ति कर लाइसेंसदाता भरेंगे, जब तक पक्ष यहाँ कुछ और न लिखें।',
        },
    },
    subletting: {
        defaultOn: true,
        title: { en: 'No subletting', mr: 'पोटभाडे नाही', hi: 'उप-किराया नहीं' },
        text: {
            en: () => 'The Licensee will not sublet or hand over the premises, or let anyone stay who is not named in this agreement, without the written consent of the Licensor.',
            mr: () => 'लायसन्सरच्या लेखी संमतीशिवाय लायसन्सी जागा पोटभाड्याने देणार नाहीत किंवा सोपवणार नाहीत, आणि या करारात नाव नसलेल्या कोणालाही राहू देणार नाहीत.',
            hi: () => 'लाइसेंसदाता की लिखित सहमति के बिना लाइसेंसधारी परिसर को उप-किराये पर नहीं देंगे या सौंपेंगे नहीं, और इस अनुबंध में जिसका नाम नहीं है उसे रहने नहीं देंगे।',
        },
    },
    termination: {
        defaultOn: true,
        title: { en: 'Ending the licence', mr: 'लायसन्स संपवणे', hi: 'लाइसेंस समाप्त करना' },
        text: {
            en: (p) => `Either party may end this licence by giving ${String(p.noticeDays)} days written notice, after any lock in period. The Licensee will hand back the premises in the condition they were received, apart from normal wear.`,
            mr: (p) => `कोणताही पक्ष, लॉक-इन कालावधीनंतर, ${String(p.noticeDays)} दिवसांची लेखी नोटीस देऊन हे लायसन्स संपवू शकतो. लायसन्सी जागा मिळाली त्या स्थितीत, नेहमीची झीज वगळता, परत देतील.`,
            hi: (p) => `कोई भी पक्ष, लॉक-इन अवधि के बाद, ${String(p.noticeDays)} दिन का लिखित नोटिस देकर यह लाइसेंस समाप्त कर सकता है। लाइसेंसधारी परिसर को, सामान्य घिसाव को छोड़कर, जैसा मिला था वैसी हालत में लौटाएँगे।`,
        },
    },
    depositRefund: {
        defaultOn: true,
        title: { en: 'Deposit refund', mr: 'अनामत रक्कम परत', hi: 'जमा राशि की वापसी' },
        text: {
            en: (p) => `The Licensor will refund the security deposit within ${String(p.refundDays)} days of the Licensee handing back the premises. Only unpaid dues and the cost of damage beyond normal wear may be deducted, with a written statement of what was deducted.`,
            mr: (p) => `लायसन्सी जागा परत दिल्यापासून ${String(p.refundDays)} दिवसांत लायसन्सर सुरक्षा अनामत रक्कम परत करतील. फक्त न भरलेली देणी आणि नेहमीच्या झिजेपलीकडील नुकसानीचा खर्च वजा करता येईल, आणि काय वजा केले त्याचे लेखी विवरण द्यावे लागेल.`,
            hi: (p) => `लाइसेंसधारी के परिसर लौटाने के ${String(p.refundDays)} दिन के भीतर लाइसेंसदाता सुरक्षा जमा राशि लौटाएँगे। केवल बकाया राशि और सामान्य घिसाव से अधिक नुकसान का खर्च काटा जा सकेगा, और क्या काटा गया इसका लिखित ब्योरा देना होगा।`,
        },
    },
    useOfPremises: {
        defaultOn: true,
        title: { en: 'Use of the premises', mr: 'जागेचा वापर', hi: 'परिसर का उपयोग' },
        text: {
            en: (p) => `The premises are for ${p.permittedUse} use only. The Licensee will not carry on a business there, keep anything dangerous, or do anything unlawful or that disturbs the neighbours.`,
            mr: (p) => `जागा फक्त ${p.permittedUse} वापरासाठी आहे. लायसन्सी तेथे व्यवसाय करणार नाहीत, धोकादायक वस्तू ठेवणार नाहीत, किंवा बेकायदेशीर किंवा शेजाऱ्यांना त्रास होईल असे काही करणार नाहीत.`,
            hi: (p) => `परिसर केवल ${p.permittedUse} उपयोग के लिए है। लाइसेंसधारी वहाँ कारोबार नहीं करेंगे, कोई ख़तरनाक चीज़ नहीं रखेंगे, और कुछ भी ग़ैरकानूनी या पड़ोसियों को परेशान करने वाला नहीं करेंगे।`,
        },
    },
    inspection: {
        defaultOn: false,
        title: {
            en: 'Inspection by the Licensor',
            mr: 'लायसन्सरकडून पाहणी',
            hi: 'लाइसेंसदाता द्वारा निरीक्षण',
        },
        text: {
            en: () => 'The Licensor may inspect the premises at a reasonable time after telling the Licensee beforehand.',
            mr: () => 'लायसन्सी यांना आधी कळवून लायसन्सर योग्य वेळी जागेची पाहणी करू शकतात.',
            hi: () => 'लाइसेंसधारी को पहले बताकर लाइसेंसदाता उचित समय पर परिसर का निरीक्षण कर सकते हैं।',
        },
    },
    inventory: {
        defaultOn: false,
        title: { en: 'Handover inventory', mr: 'हस्तांतरण यादी', hi: 'हस्तांतरण सूची' },
        text: {
            en: () => 'The fittings, furniture and appliances handed over are listed in an inventory signed by both parties. The Licensee will return them in the same condition, apart from normal wear.',
            mr: () => 'दिलेल्या फिटिंग्ज, फर्निचर आणि उपकरणांची यादी दोन्ही पक्षांनी सही केलेल्या यादीत आहे. लायसन्सी ती नेहमीची झीज वगळता त्याच स्थितीत परत करतील.',
            hi: () => 'सौंपी गई फ़िटिंग, फ़र्नीचर और उपकरणों की सूची दोनों पक्षों के हस्ताक्षर वाली सूची में है। लाइसेंसधारी उन्हें सामान्य घिसाव को छोड़कर उसी हालत में लौटाएँगे।',
        },
    },
    policeVerification: {
        defaultOn: true,
        title: {
            en: 'Police verification details',
            mr: 'पोलीस पडताळणीसाठी माहिती',
            hi: 'पुलिस सत्यापन के लिए जानकारी',
        },
        text: {
            en: () => 'The Licensee will give the Licensor the identity details and documents needed for police verification of the Licensee and the people staying with them. This agreement does not say that any verification has been filed or completed.',
            mr: () => 'लायसन्सी आणि त्यांच्यासोबत राहणाऱ्या व्यक्तींच्या पोलीस पडताळणीसाठी लागणारी ओळखीची माहिती आणि कागदपत्रे लायसन्सी लायसन्सरला देतील. कोणतीही पडताळणी दाखल केली आहे किंवा पूर्ण झाली आहे असे हा करार सांगत नाही.',
            hi: () => 'लाइसेंसधारी और उनके साथ रहने वालों के पुलिस सत्यापन के लिए ज़रूरी पहचान की जानकारी और दस्तावेज़ लाइसेंसधारी लाइसेंसदाता को देंगे। यह अनुबंध यह नहीं कहता कि कोई सत्यापन दाखिल या पूरा हो चुका है।',
        },
    },
};
/** The keys on when the broker has not chosen. */
export const DEFAULT_CLAUSES = CLAUSE_KEYS.filter((key) => CLAUSES[key].defaultOn);
export const DEFAULT_REFUND_DAYS = 30;
export function isClauseKey(value) {
    return CLAUSE_KEYS.includes(value);
}
export function clauseText(key, lang, params) {
    return CLAUSES[key].text[lang](params);
}
export function clauseTitle(key, lang = 'en') {
    return CLAUSES[key].title[lang];
}
//# sourceMappingURL=agreement-clauses.js.map